import dayjs from "dayjs";
import * as yup from "yup";
import {exists} from "../../utils/helpers.js";
import {addField, COMPONENT, getFields, GROUP, labelAlreadyExists} from "../../utils/builder.js";
import {IDX_TO_DAY_DAYJS} from "../schedule/helpers.js";

export {
  resolveFieldParentName,
  transformFormula,
  buildFormulaString,
  formatFormulaToSave
} from "./formulaHelpers.js";

/**
 * @param {object} byId
 * @param {string} name
 * @returns {boolean}
 */
export const isActiveBuilderElement = (byId, name) => !!byId?.[name] && !byId[name].deleted;

const memberName = member => {
  if (!member) return "";
  if (member.firstName || member.lastName)
    return [member.firstName, member.lastName].filter(Boolean).join(" ");
  return member.label || member.email || "";
};

/**
 * Who a restriction is limited to, and which fields it covers.
 * Group labels are not stored on the saved subset, so field labels are the useful summary.
 * @param {object} restriction
 * @returns {{memberNames: string[], fieldLabels: string[], memberKind: "roles"|"users"}}
 */
export const describeRestriction = restriction => {
  const members = restriction?.members || [];
  const memberNames = members.map(memberName).filter(Boolean);
  const isRole = Boolean(members[0]?.label) && !members[0]?.publicId;
  const {allIds = [], byId = {}} = restriction?.builder || {};
  const fieldLabels = getFields(allIds, byId)
    .map(field => field.label)
    .filter(Boolean);

  return {
    memberNames,
    fieldLabels,
    memberKind: isRole ? "roles" : "users"
  };
};

/**
 * Field-restriction members who are not on the checksheet visibility list.
 * Admin bypass is not coverage: opening a checksheet directly does not use it.
 * @param {object[]} restrictTo visibility users ({publicId}) or roles ({id})
 * @param {object[]} restrictions field restrictions with `label` and `members`
 * @param {Set<string>} coveredUserIds publicIds known to hold a visibility role
 *   (for user members whose `role` is not loaded)
 * @returns {{restrictionLabel: string, memberName: string}[]}
 */
export const getVisibilityConflicts = (restrictTo, restrictions, coveredUserIds = new Set()) => {
  if (!restrictTo?.length) return [];

  const visibleByUser = Boolean(restrictTo[0]?.publicId);
  const visibleIds = new Set(restrictTo.map(({publicId, id}) => (visibleByUser ? publicId : id)));

  const canOpen = member => {
    if (!member?.publicId) return !visibleByUser && visibleIds.has(member?.id);
    if (visibleByUser) return visibleIds.has(member.publicId);
    return visibleIds.has(member.role?.id) || coveredUserIds.has(member.publicId);
  };

  return (restrictions || []).flatMap(({label, members}) =>
    (members || [])
      .filter(member => !canOpen(member))
      .map(member => ({restrictionLabel: label, memberName: memberName(member)}))
  );
};

/** External checksheets are emailed on a due schedule; None/unset has no due date. */
export const canSetExternalChecksheet = frequency => {
  const name = typeof frequency === "string" ? frequency : frequency?.name;
  return Boolean(name && name !== "none");
};

/**
 * @param {object} byId
 * @param {string[]} children
 * @returns {string[]}
 */
export const getActiveChildIds = (byId, children = []) =>
  children.filter(name => isActiveBuilderElement(byId, name));

/**
 * @param {object} builder
 * @param {{requireToggle?: boolean}} [options]
 * @returns {array}
 */
export const getLinkedGroupOptions = (builder, {requireToggle = true} = {}) => {
  const {allIds = [], byId = {}} = builder || {};

  return allIds
    .map(id => byId[id])
    .filter(item => item?.element === GROUP && (!requireToggle || item.toggle));
};

/**
 * @param {object} builder
 * @param {string} [parentGroupName]
 * @param {{requireToggle?: boolean, predicate?: function}} [options]
 * @returns {array}
 */
export const getLinkedComponentOptions = (
  builder,
  parentGroupName = "",
  {requireToggle = true, predicate = () => true} = {}
) => {
  const {allIds = [], byId = {}} = builder || {};

  const matches = item =>
    item?.element === COMPONENT && (!requireToggle || item.toggle) && predicate(item);

  if (parentGroupName) {
    return (byId[parentGroupName]?.children || [])
      .map(id => byId[id])
      .filter(item => item && matches(item) && item.parentName === parentGroupName);
  }

  const results = [];
  const seen = new Set();

  allIds.forEach(id => {
    const item = byId[id];
    if (!item) return;

    if (item.element === GROUP) {
      (item.children || []).forEach(childId => {
        const child = byId[childId];
        if (child && matches(child) && !seen.has(childId)) {
          seen.add(childId);
          results.push(child);
        }
      });
    } else if (matches(item) && !seen.has(id)) {
      seen.add(id);
      results.push(item);
    }
  });

  return results;
};

/**
 * @param {object} checksheetBuilder
 * @param {object} [facilityBuilder]
 * @returns {Array<{value: string, label: string, name?: string}>}
 */
export const getPlacementOptions = (checksheetBuilder, facilityBuilder) => {
  const {allIds = [], byId = {}} = checksheetBuilder || {};
  const facilityById = facilityBuilder?.byId;

  return [
    {value: "_TOP_", name: "Top of builder"},
    ...allIds
      .filter(id => {
        const element = byId[id];
        if (!element || element.deleted || element.stale || element.element !== GROUP) return false;
        if (facilityById?.[id] && !isActiveBuilderElement(facilityById, id)) return false;
        return true;
      })
      .map(id => ({
        value: byId[id].name,
        label: byId[id].label
      }))
  ];
};

/**
 * Default linked group/component values for the edit-field form.
 * @param {object} builder
 * @param {object} targetElement
 * @returns {{linkedGroup: string, linkedComponent: string}}
 */
export const getFieldLinkDefaults = (builder, targetElement) => {
  const linkedGroups = builder.allIds
    .map(id => builder.byId[id])
    .filter(el => el?.element === "group" && el?.toggle);

  const groups = new Set(linkedGroups.map(item => item.name));
  const parentElement =
    targetElement.parentName && targetElement.parentName in builder.byId
      ? builder.byId[targetElement.parentName]
      : null;

  if (parentElement?.element === "component") {
    const groupName = parentElement.parentName;
    return {
      linkedGroup: groupName && groups.has(groupName) ? groupName : "",
      linkedComponent: targetElement.parentName
    };
  }

  if (parentElement?.element === "group" && groups.has(targetElement.parentName)) {
    return {
      linkedGroup: targetElement.parentName,
      linkedComponent: ""
    };
  }

  if (groups.has(targetElement.parentName)) {
    return {linkedGroup: targetElement.parentName, linkedComponent: ""};
  }

  return {linkedGroup: "", linkedComponent: ""};
};

/**
 * @param {object} builder
 * @param {string} fieldId
 * @returns {array}
 */
export const fieldsOnLevel = (builder, fieldId) => {
  const {byId, allIds} = builder;

  const field = byId[fieldId];

  if (!field) return [];

  const fieldsOnSameLevel =
    field.parentName && field.parentName !== "" && byId[field.parentName]
      ? byId[field.parentName].children
      : allIds;

  return fieldsOnSameLevel.filter(
    id => id in byId && byId[id].element === "field" && id !== fieldId
  );
};

/**
 * @param {object} builder
 * @param {string} fieldId
 * @returns {array}
 */
export const validNumberFieldOptions = (builder, fieldId) => {
  const {byId} = builder;
  const fieldsOnSameLevel = fieldsOnLevel(builder, fieldId);

  return fieldsOnSameLevel
    .filter(name => byId[name].type === "number")
    .map(name => ({
      name: name,
      label: byId[name].label
    }));
};

/**
 * @param {object} builder
 * @param {string} fieldId
 * @returns {array}
 */
export const validFieldOptions = (builder, fieldId) => {
  const {byId} = builder;
  const result = fieldsOnLevel(builder, fieldId);

  return result.map(name => ({
    name: name,
    label: byId[name].label
  }));
};

/**
 * @param {Array.<string>} ids
 * @param {object} builder
 * @param {object} result
 * @returns {object}
 */
export const getIndexedBuilderFields = (ids, builder, result = {}, count = 0) => {
  ids.map(id => {
    const item = builder.byId[id];
    result[id] = count++;

    if (item && item.children && item.children.length > 0) {
      count = getIndexedBuilderFields(item.children, builder, result, count);
    }
  });

  return count;
};

/**
 * @param {HTMLElement} selector
 * @param {object} yOffset
 * @param {HTMLElement} parent
 * @returns
 */
export const scrollTo = (selector, yOffset = -150, parent = window, behavior = "smooth") => {
  if (selector) {
    const y = selector.parentElement.getBoundingClientRect().top + yOffset;
    parent.scrollBy({behavior, top: y});
  }
};

export const scrollToScrollTop = (scrollTop, parent, behavior = "smooth") => {
  const y = parent.scrollTop - scrollTop;
  parent.scrollBy({behavior, top: y});
};

export const evaluateTemporalConditional = (value, depends, compare) => {
  if (depends === "weekday") {
    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const weekday = value.get("day");
    return compare.includes(weekdays[weekday]);
  }

  // otherwise, absolute date
  const year = value.get("year");
  const month = value.get("month") + 1; // increment by 1 because dayjs is 0-based
  const date = value.get("date");
  for (let i = 0; i < compare.length; i++) {
    const frequencyParts = compare[i].split("_");
    const frequency = frequencyParts[1] || null;
    const end = frequencyParts[2] || null;

    const parts = frequencyParts[0].split("-");
    const targetYear = parseInt(parts[0], 10);
    const targetMonth = parseInt(parts[1], 10);
    const targetDate = parseInt(parts[2], 10);

    if (!frequency && targetYear === year && targetMonth === month && targetDate === date)
      return true;

    if (frequency === "weekly") {
      const diff = dayjs(value).diff(dayjs(parts), "weeks");
      const target = dayjs(parts).add(diff, "weeks");
      return target.get("date") === date;
    }

    if (frequency === "monthly") {
      let target = dayjs(parts);
      if (end) target = dayjs(value).endOf("month");
      return target.get("date") === date;
    }

    if (frequency === "annually" && targetMonth === month && targetDate === date) return true;
  }
  return false;
};

/**
 * Parse condtion object and evaluate
 * @param {string} value
 * @param {string} check
 * @param {string} compare
 * @param {object} depends
 * @returns {bool}
 */
export const evaluateConditional = (value, check, compare, depends = null, operator = null) => {
  let checkArray = check;
  let compareArray = compare;

  if (!Array.isArray(checkArray)) checkArray = [checkArray];
  if (!Array.isArray(compareArray)) compareArray = [compareArray];

  if (depends === "weekday" || depends === "date")
    return evaluateTemporalConditional(value, depends, compareArray);

  // Multiselect all unchecked
  if (check && compare && operator === "or" && value === null)
    for (let i = 0; i < compare.length; i++) {
      if (compare[i] === "unchecked") return false;
    }

  if (
    (value === undefined ||
      value === null ||
      value === "" ||
      check === undefined ||
      compare === undefined) &&
    depends?.type !== "upload"
  )
    return false;

  // start result at true only for 'and', since a single false result means return false
  let result = operator === "and";

  for (let i = 0; i < checkArray.length; i++) {
    let parsedCompare = compareArray[i];
    const currentCheck = checkArray[i];
    let parsedValue = value;

    parsedCompare = parsedCompare?.includes("Other:")
      ? parsedCompare.split("Other: ")[1]
      : parsedCompare;
    parsedCompare = parsedCompare === "true" ? true : parsedCompare;
    parsedCompare = parsedCompare === "false" ? false : parsedCompare;

    if (depends?.type === "confirm") parsedCompare = parsedCompare ? "confirmed" : false;

    if (
      (depends?.type === "number" || depends?.type === "generated" || depends?.type === "range") &&
      exists(parsedCompare) &&
      !Number.isNaN(Number.parseFloat(parsedCompare))
    )
      parsedCompare = Number.parseFloat(parsedCompare);

    // Use exists() so numeric 0 is still coerced (truthy check would skip it).
    if (
      (depends?.type === "number" || depends?.type === "generated" || depends?.type === "range") &&
      exists(parsedValue) &&
      !Number.isNaN(Number.parseFloat(parsedValue))
    )
      parsedValue = Number.parseFloat(parsedValue);

    if (depends?.type === "switch") parsedValue = parsedValue ? depends.on : depends.off;

    if (depends?.type === "time") {
      const compareTime = parsedCompare.split(":");
      const compareHr = compareTime[0];
      const compareMn = compareTime[1];
      const compareDate = new Date();
      compareDate.setHours(compareHr);
      compareDate.setMinutes(compareMn);
      parsedCompare = compareDate;

      const valueTime = parsedValue.split(":");
      const valueHr = valueTime[0];
      const valueMn = valueTime[1];
      const valueDate = new Date();
      valueDate.setHours(valueHr);
      valueDate.setMinutes(valueMn);
      parsedValue = valueDate;
    }

    if (depends?.type === "date") {
      parsedCompare = new Date(`${parsedCompare}T00:00:00`);
      parsedValue = new Date(`${parsedValue}T00:00:00`);

      parsedCompare = parsedCompare.getTime();
      parsedValue = parsedValue.getTime();

      if (Number.isNaN(parsedCompare) || Number.isNaN(parsedValue)) parsedValue = null;
    }

    if (currentCheck === "contains" && exists(parsedValue))
      // Default Comparisons
      result = parsedValue.includes(parsedCompare);
    if (currentCheck === "===" && exists(parsedValue)) result = parsedValue === parsedCompare;
    if (currentCheck === "!==" && exists(parsedValue)) result = parsedValue !== parsedCompare;
    if (currentCheck === ">" && exists(parsedValue)) result = parsedValue > parsedCompare;
    if (currentCheck === "<" && exists(parsedValue)) result = parsedValue < parsedCompare;
    if (currentCheck === ">=" && exists(parsedValue)) result = parsedValue >= parsedCompare;
    if (currentCheck === "<=" && exists(parsedValue)) result = parsedValue <= parsedCompare;

    // Multiselect overrides
    if (depends?.type === "multiselect" && parsedValue) {
      if (parsedCompare === "checked") result = parsedValue.length === depends.options.length;
      if (parsedCompare === "unchecked") result = parsedValue.length === 0;
      if (parsedCompare === "one") result = parsedValue.length === 1;
      if (parsedCompare === "more") result = parsedValue.length > 0;
    }

    if (depends && depends.type === "upload") {
      if (currentCheck === "===") result = !!value;
      if (currentCheck === "!==") result = !value;
    }

    if (result && (!operator || operator === "or")) return result;
    if (!result && operator === "and") return result;
  }

  return operator === "and";
};

export const evaluateRange = (value, element, qualifier = null, infinite = false) => {
  const {min, max, aMin, aMax, strictMin, strictMax} = element;

  const valueParsed = exists(value) ? Number.parseFloat(value) : null;
  const minParsed = exists(min) ? Number.parseFloat(min) : null;
  const maxParsed = exists(max) ? Number.parseFloat(max) : null;
  const aMinParsed = exists(aMin) ? Number.parseFloat(aMin) : null;
  const aMaxParsed = exists(aMax) ? Number.parseFloat(aMax) : null;

  let overMax = false;
  let belowMin = false;

  if (!exists(valueParsed) && !infinite) return false;

  if (aMinParsed !== null) {
    if (["<", "ND", "≤"].includes(qualifier))
      belowMin = !(minParsed !== null && minParsed >= aMinParsed);
    else if (strictMin)
      belowMin = valueParsed < aMinParsed || (valueParsed === aMinParsed && qualifier !== ">");
    else belowMin = valueParsed < aMinParsed;
  }

  if (aMaxParsed !== null) {
    if ([">", "≥"].includes(qualifier) || infinite)
      overMax = !(maxParsed !== null && maxParsed <= aMaxParsed);
    else if (strictMax)
      overMax =
        valueParsed > aMaxParsed ||
        (valueParsed === aMaxParsed && !["<", "ND"].includes(qualifier));
    else overMax = valueParsed > aMaxParsed;
  }

  return overMax || belowMin;
};

/**
 * Convert HH:MM (24h) string to fractional hours. Returns null if unparseable.
 * @param {string} value
 * @returns {number|null}
 */
export const parseTimeToHours = value => {
  if (!value || typeof value !== "string" || !value.includes(":")) return null;
  const [hour, minute] = value.split(":");
  const hourParsed = Number.parseInt(hour, 10);
  const minParsed = Number.parseInt(minute, 10);
  if (Number.isNaN(hourParsed) || Number.isNaN(minParsed)) return null;
  return hourParsed + minParsed / 60;
};

/**
 * Elapsed formulas are stored as [stop, "-", start]. Returns true when start is
 * later on the clock than stop (same-day ordering). Overnight wrap in InputGenerated
 * still yields a positive duration in that case — this check is for explicit UAP.
 * @param {object} field builder field (elapsed / generated time formula)
 * @param {object} values form values
 * @returns {boolean}
 */
export const isElapsedStartAfterStop = (field, values) => {
  if (!field?.alertStartAfterStop || !field?.formula?.length) return false;

  const timeParts = field.formula.filter(part => part?.type === "variable");
  if (timeParts.length < 2) return false;

  const stopName = timeParts[0].value;
  const startName = timeParts[timeParts.length - 1].value;
  const stopHours = parseTimeToHours(values?.[stopName]);
  const startHours = parseTimeToHours(values?.[startName]);

  if (!exists(stopHours) || !exists(startHours)) return false;
  return startHours > stopHours;
};

export const evaluateInvalid = (value, invalid) => {
  if (invalid && Array.isArray(invalid)) return invalid.includes(value);
  return invalid === value;
};

export const recurseToggle = (ids, builder, setTo) => {
  if (!builder) return null;

  const {byId} = builder;

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (byId[id] !== undefined) {
      // Always apply setTo — toggle-all must flip elements that already have an explicit toggle.
      byId[id].toggle = setTo;
      if (!byId[id].toggle) byId[id].preview = false;

      if (byId[id].children?.length > 0) {
        builder = recurseToggle(byId[id].children, builder, setTo);
      }
    }
  }

  return builder;
};

export const getToggleState = (ids, builder) => {
  if (!builder) return true;
  const {byId} = builder;
  let hasToggledOff = false;

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (byId[id] !== undefined) {
      if (!byId[id].toggle) return true;

      if (byId[id].children?.length > 0) {
        hasToggledOff = getToggleState(byId[id].children, builder);
        if (hasToggledOff) return true;
      }
    }
  }
  return hasToggledOff;
};

/**
 * Get full name with ancestors
 * @param {object} element
 * @param {object} builder
 * @returns {string}
 */
export const getAncestryName = (element, builder) => {
  let curr = element;
  let currLabel = curr ? curr.label : "";
  while (curr && curr.parentName) {
    curr = builder.byId[curr.parentName];
    if (curr) currLabel = `${curr.label} ${currLabel}`;
  }
  return currLabel;
};

/**
 * @param {object} updated
 * @param {object} nameMap
 */
export const resolveDependencyMismatches = (updated, nameMap) => {
  const {byId} = updated;
  const formulasToReset = [];
  Object.entries(nameMap)
    .filter(([, newName]) => newName in byId)
    .forEach(([oldName, newName]) => {
      // updating names in depends from field{idx} -> generated key
      if (byId[newName].depends)
        byId[newName].depends = byId[newName].depends.map(dependency => nameMap[dependency]);

      // updating names in trueDepends from field{idx} -> generated key
      if (byId[newName].trueDepends)
        byId[newName].trueDepends = byId[newName].trueDepends.map(
          dependency => nameMap[dependency]
        );

      if (byId[newName].formula) {
        formulasToReset.push(newName);
        const newFormula = [];
        // updating names in formula from field{idx} -> generated key
        byId[newName].formula.forEach(part => {
          newFormula.push({
            ...part,
            value: part.value in nameMap ? nameMap[part.value] : part.value
          });
        });
        byId[newName].formula = newFormula;
      }

      if (byId[newName].trueFormula) {
        const newFormula = [];
        // updating names in trueFormula from field{idx} -> generated key
        byId[newName].trueFormula.forEach(part => {
          newFormula.push({
            ...part,
            value: part.value in nameMap ? nameMap[part.value] : part.value
          });
        });
        byId[newName].trueFormula = newFormula;
      }
      if (byId[newName].condition && byId[newName].condition.list) {
        // updating names in condition from field{idx} -> generated key
        const newList = [];
        byId[newName].condition.list.forEach(cond => {
          if (cond.depends in nameMap) {
            newList.push({
              ...cond,
              depends: cond.depends in nameMap ? nameMap[cond.depends] : cond.depends
            });
          } else {
            newList.push({...cond});
          }
        });
        byId[newName].condition = {
          ...byId[newName].condition,
          list: [...newList.map(cond => ({...cond}))]
        };
      }

      if (byId[newName].parentName in byId && byId[byId[newName].parentName].help) {
        byId[byId[newName].parentName].help = byId[byId[newName].parentName].help.map(h => {
          if (h.id === oldName) return {key: byId[newName].label, value: h.value, id: newName};
          return h;
        });
      }

      if (byId[newName].formulaString)
        // updating names in formulaString from field{idx} -> generated key
        Object.entries(nameMap).forEach(([o, n]) => {
          if (byId[newName].formulaString.includes(o))
            byId[newName].formulaString = byId[newName].formulaString.replaceAll(o, n);
        });
    });
};

/**
 * Walk condition dependencies from `id`. Returns false if `id` is already on
 * the current path (a true cycle). Duplicate OR rows on the same field are not cycles.
 * @param {string} id
 * @param {object} builder
 * @param {string[]} path
 * @returns {boolean}
 */
export const descendDependencies = (id, builder, path) => {
  if (path.includes(id)) return false;
  const list = builder.byId[id]?.condition?.list;
  if (!list?.length) return true;

  const nextPath = [...path, id];
  for (let i = 0; i < list.length; i++) {
    if (!descendDependencies(list[i].depends, builder, nextPath)) return false;
  }
  return true;
};

/**
 * Find `.when()` dependency edges that close a loop in a yup object shape, to pass as the
 * `excludes` argument of `yup.object().shape(fields, excludes)`. Yup throws "Cyclic dependency"
 * when sorting fields whose conditions depend on each other or on themselves. Excluding an edge
 * only changes the field sort order; `.when()` still reads sibling values from the parent.
 * @param {object} fields - yup schemas keyed by field name
 * @returns {Array<[string, string]>} [field, dependency] edges to exclude
 */
export const getCyclicDependencyEdges = fields => {
  const graph = Object.fromEntries(
    Object.entries(fields).map(([key, schema]) => [
      key,
      (schema?.deps || []).map(path => path.split(/[.[]/)[0])
    ])
  );
  const VISITING = 1;
  const DONE = 2;
  const state = {};
  const edges = [];

  const visit = key => {
    state[key] = VISITING;
    (graph[key] || []).forEach(dep => {
      if (state[dep] === VISITING) edges.push([key, dep]);
      else if (!state[dep]) visit(dep);
    });
    state[key] = DONE;
  };

  Object.keys(graph).forEach(key => {
    if (!state[key]) visit(key);
  });
  return edges;
};

// `trigger` is left out on purpose: `depends` already implies it (field and component keys are
// unique, weather/weekday/date use fixed names), and legacy rows saved without a trigger would
// otherwise never match and get duplicated.
const sameConditionRow = (a, b) => {
  const sameValues = (x, y) => {
    if (!x || !y || Array.isArray(x) !== Array.isArray(y)) return false;
    if (!Array.isArray(x)) return x === y;
    return x.length === y.length && x.every((value, i) => value === y[i]);
  };
  return (
    a.depends === b.depends &&
    a.operator === b.operator &&
    sameValues(a.check, b.check) &&
    sameValues(a.compare, b.compare)
  );
};

/**
 * Apply a bulk condition to the selected fields, merging rows into existing conditions.
 * Every field gets its own condition object and list so later merges cannot leak rows
 * onto fields outside the selection.
 * @param {object} builder
 * @param {string[]} fieldNames
 * @param {object} condition
 * @returns {object} builder
 */
export const applyBulkCondition = (builder, fieldNames, condition) => {
  const byId = {...builder.byId};
  fieldNames.forEach(name => {
    const field = byId[name];
    const existing = field.condition?.list?.length ? field.condition : null;
    const list = existing ? existing.list.map(row => ({...row})) : [];
    condition.list.forEach(row => {
      if (!existing || !list.some(current => sameConditionRow(current, row)))
        list.push({...row});
    });
    byId[name] = {
      ...field,
      condition: {...(existing || condition), action: condition.action, list}
    };
  });
  return {...builder, byId};
};

/**
 * Determine whether formula makes circular references
 * @param {array} formula
 * @param {object} builder
 * @param {object} targetElement
 * @param {number} level
 * @returns {bool}
 */
export const circularFormulaTest = (formula, builder, targetElement, level = 0) => {
  for (let i = 0; i < formula.length; i++) {
    if (
      formula[i].value in builder.byId &&
      builder.byId[formula[i].value].type === "generated" &&
      !formula[i].previous
    ) {
      if (level && formula[i].value === targetElement.name) return false;

      const element = builder.byId[formula[i].value];
      // recurse until hitting base level (number field)
      const result = circularFormulaTest(element.formula, builder, targetElement, level + 1);
      if (!result) return false;
    }
  }
  return true;
};

/**
 * Validate formula array, returning true if valid, false otherwise
 * @param {array} formula
 * @param {object} builder
 * @param {object} targetElement
 * @returns {bool}
 */
export const validateFormula = formula => {
  if (!formula || formula.length < 3) return false;
  let prevSymbol;
  let currSymbol;
  let nextSymbol;
  let grouping = 0;

  for (let i = 0; i < formula.length; i++) {
    if (i !== 0) prevSymbol = formula[i - 1];
    currSymbol = formula[i];
    if (i !== formula.length - 1) nextSymbol = formula[i + 1];
    else nextSymbol = null;

    if (currSymbol.value === "(") grouping += 1;
    if (currSymbol.value === ")") {
      if (grouping === 0) return false;
      grouping -= 1;
    }

    if (
      (!prevSymbol || prevSymbol.value === "(") &&
      !(currSymbol.type === "variable" || currSymbol.value === "(")
    )
      return false;

    if (
      (!nextSymbol || nextSymbol.value === ")") &&
      !(currSymbol.type === "variable" || currSymbol.value === ")")
    )
      return false;

    if (prevSymbol && prevSymbol.value === ")" && currSymbol.type !== "operation") return false;

    if (
      prevSymbol &&
      prevSymbol.type === "operation" &&
      !(currSymbol.type === "variable" || currSymbol.value === "(")
    )
      return false;

    if (
      prevSymbol &&
      prevSymbol.type === "variable" &&
      !(currSymbol.type === "operation" || currSymbol.value === ")")
    )
      return false;
  }

  if (grouping > 0) return false;

  return true;
};

export const WEATHER_CONDITION_DEPENDS = [
  "rainfall",
  "cumulative",
  "precipitationChance",
  "tempMin",
  "tempMax",
  "tempAvg"
];

/** Shared weather depends options (builder conditions + notifications). */
export const WEATHER_DEPENDS_OPTIONS = [
  {name: "rainfall", label: "Yesterday's Rainfall"},
  {name: "cumulative", label: "Yesterday's Cumulative Rainfall"},
  {name: "tempMin", label: "Yesterday's Min Temperature"},
  {name: "tempMax", label: "Yesterday's Max Temperature"},
  {name: "tempAvg", label: "Yesterday's Avg Temperature"},
  {name: "precipitationChance", label: "Today's Precipitation Chance"}
];

export const WEATHER_DEPENDS_LABELS = Object.fromEntries(
  WEATHER_DEPENDS_OPTIONS.map(({name, label}) => [name, label])
);

export const getWeatherDependsLabel = depends =>
  WEATHER_DEPENDS_LABELS[depends === "precipitationchance" ? "precipitationChance" : depends] ||
  depends;

export const getWeatherUnits = depends => {
  if (depends === "precipitationChance" || depends === "precipitationchance") return "%";
  if (["tempMin", "tempMax", "tempAvg"].includes(depends)) return "°F";
  if (depends === "rainfall" || depends === "cumulative") return "in.";
  return "";
};

export const isWeatherConditionDepends = depends =>
  WEATHER_CONDITION_DEPENDS.includes(depends) || depends === "precipitationchance";

export const WEATHER_CHECK_OPTIONS = [
  {label: "is", name: "==="},
  {label: "is not", name: "!=="},
  {label: "is greater than", name: ">"},
  {label: "is less than", name: "<"},
  {label: "is greater than or equal to", name: ">="},
  {label: "is less than or equal to", name: "<="}
];

/** Strip legacy "Other: " prefix from weather compare values. */
export const normalizeWeatherCompareValue = compare => {
  if (compare == null || typeof compare !== "string") return compare;
  return compare.includes("Other: ") ? compare.replace("Other: ", "") : compare;
};

export const getWeatherConditionValue = (context, depends) => {
  if (!context) return null;
  if (depends === "precipitationChance" || depends === "precipitationchance") {
    if (typeof context.precipitationChance === "number") return context.precipitationChance;

    const parsed = parseInt(context.precipitationChance, 10);
    if (!Number.isNaN(parsed)) return parsed;

    if (typeof context.weather?.precipitationChance === "number")
      return context.weather.precipitationChance;
    return null;
  }
  if (
    depends === "rainfall" ||
    depends === "cumulative" ||
    depends === "tempMin" ||
    depends === "tempMax" ||
    depends === "tempAvg"
  )
    // Support both RenderChecksheet's flat spread ({...weather, precipitationChance})
    // and a nested {weather: {...}} shape.
    return context.weather?.[depends] ?? context[depends] ?? null;
  return null;
};

export const getPrecipitationHideCondition = condition => {
  if (!condition?.action?.includes("Hide")) return null;
  return condition.list?.find(item => item.depends === "precipitationChance") || null;
};

export const getPrecipitationBlockMessage = (label, threshold) => {
  const pct = Math.round(Number(threshold));
  const fieldLabel = label?.toLowerCase() || "field";
  return `No, sorry. There is a ${pct}% chance (or more) of rain so you cannot use the ${fieldLabel} today.`;
};

export const isPrecipitationConditionActive = (condition, context) => {
  const entry = condition?.list?.find(item => item.depends === "precipitationChance");
  if (!entry) return null;

  const value = getWeatherConditionValue(context, "precipitationChance");
  const active = evaluateConditional(
    value,
    entry.check,
    entry.compare,
    {type: "number"},
    entry.operator
  );
  if (!active) return null;

  const threshold = Array.isArray(entry.compare) ? entry.compare[0] : entry.compare;
  return threshold;
};

export const collectTransferDependencyClosure = (elementName, sourceById, destById) => {
  if (!elementName || !sourceById || !destById) return new Set();
  const required = new Set();
  const expanded = new Set();

  const visitDeps = name => {
    if (expanded.has(name)) return;
    expanded.add(name);
    if (!destById[name]) {
      const el = sourceById[name];

      const consume = ref => {
        required.add(ref);
        visitDeps(ref);
      };

      el.condition?.list?.forEach(({depends}) => consume(depends));
      (el.depends || []).forEach(consume);
      (el.trueDepends || []).forEach(consume);
      (el.formula || []).forEach(part => {
        if (part?.value && sourceById[part.value]) consume(part.value);
      });
    }
  };

  visitDeps(elementName);
  required.delete(elementName);

  return required;
};

/**
 * Merge dependency subtree nodes from source into destination (caller passes a mutable deep copy).
 * Children lists are trimmed to this transfer closure or nodes already present in dest.
 *
 * @param {{byId: object, allIds: string[]}} destBuilder — deep copy of destination builder
 * @param {Record<string, object>} sourceById
 * @param {string[]} closureNames values from collectTransferDependencyClosure filtered to missing-in-dest
 * @returns {{byId: object, allIds: string[]}}
 */
export const mergeTransferDependenciesIntoBuilder = (
  destBuilder,
  sourceById,
  closureNames,
  linkedParent
) => {
  const updated = destBuilder;
  const closure = new Set(closureNames);

  closure.forEach(name => {
    if (updated.byId[name]) return;
    const src = sourceById[name];
    if (!src) return;

    updated.byId[name] = {...src, parentName: linkedParent in updated.byId ? linkedParent : ""};

    const {parentName} = updated.byId[name];

    if (!parentName || parentName === "") {
      if (!updated.allIds.includes(name)) updated.allIds.push(name);
    } else if (updated.byId[parentName]) {
      const parent = updated.byId[parentName];
      const ch = parent.children ? [...parent.children] : [];
      if (!ch.includes(name)) {
        updated.byId[parentName] = {...parent, children: [...ch, name]};
      }
    }
  });

  return updated;
};

export const getConditionValuesFromForm = (
  condition,
  formValues,
  dateForConditional = null,
  weatherContext = null
) =>
  condition.list.map(cond => {
    if (cond.trigger === "parentDisabled") return formValues[`${cond.depends}_state`];
    if (isWeatherConditionDepends(cond.depends))
      return getWeatherConditionValue(weatherContext, cond.depends);
    if (["weekday", "date"].includes(cond.depends)) return dateForConditional;
    return formValues[cond.depends];
  });

const getFormValuesAtValidate = testContext =>
  testContext.options?.context ||
  testContext.from?.[testContext.from.length - 1]?.value ||
  testContext.parent ||
  {};

/**
 * Generated field validation: required + numeric only when getConditionRequired is true.
 */
export const buildGeneratedConditionalValidation = (
  field,
  {getConditionRequired, dateForConditional, weatherContext, isFieldVisible}
) => {
  const {name, label, condition, min, max, restrictedRangeException} = field;

  // Generated/formula fields have no explicit `required` property. They are required by
  // default (a visible formula must resolve to a value); only an explicit `false` opts out.
  // This matters both for fields with no condition, and for "Hide field"/"Show field"
  // visibility conditions where getConditionRequired falls back to this base required flag.
  const required = field.required ?? true;

  // Operands the formula reads from. If any is hidden, the formula cannot be expected to
  // resolve, so it should not be required.
  const operands = [...new Set([...(field.depends || []), ...(field.trueDepends || [])])].filter(
    dep => dep && dep !== name
  );

  const anyOperandHidden = () =>
    typeof isFieldVisible === "function" && operands.some(dep => !isFieldVisible(dep));

  const isRequired = formValues => {
    if (anyOperandHidden()) return false;
    if (!condition?.list?.length) return !!required;
    const values = getConditionValuesFromForm(
      condition,
      formValues,
      dateForConditional,
      weatherContext
    );
    return getConditionRequired(condition, values, required, dateForConditional, weatherContext);
  };

  return yup
    .mixed()
    .nullable()
    .test({
      name: `${name}-required`,
      message: `${label} is a required field.`,
      test(val) {
        if (!isRequired(getFormValuesAtValidate(this))) return true;
        if (val === "_NO_PREVIOUS" || val === "_DEP_IS_INF") return true;
        return exists(val);
      }
    })
    .test({
      name: `${name}-numeric`,
      message: "Value must be a number.",
      test(val) {
        if (!isRequired(getFormValuesAtValidate(this))) return true;
        if (val === "_NO_PREVIOUS" || val === "_DEP_IS_INF") return true;
        if (!exists(val)) return true;
        const parsed = typeof val === "number" ? val : Number(val);
        return !Number.isNaN(parsed);
      }
    })
    .test({
      name: `${name}-min`,
      message: restrictedRangeException || `Value must be > ${min}.`,
      test(val) {
        if (!isRequired(getFormValuesAtValidate(this))) return true;
        if (val === "_NO_PREVIOUS" || val === "_DEP_IS_INF") return true;
        if (exists(val) && exists(min)) {
          const parsed = Number.parseFloat(val);
          if (!Number.isNaN(parsed) && parsed < min) return false;
        }
        return true;
      }
    })
    .test({
      name: `${name}-max`,
      message: restrictedRangeException || `Value must be < ${max}.`,
      test(val) {
        if (!isRequired(getFormValuesAtValidate(this))) return true;
        if (val === "_NO_PREVIOUS" || val === "_DEP_IS_INF") return true;
        if (exists(val) && exists(max)) {
          const parsed = Number.parseFloat(val);
          if (!Number.isNaN(parsed) && parsed > max) return false;
        }
        return true;
      }
    });
};

export const buildGeneratedNumberSchema = (label, transformNumber) =>
  yup
    .number()
    .nullable(true)
    .transform(transformNumber)
    .typeError("Value must be a number.")
    .required(`${label} is a required field.`);

/**
 * @param {object} builder
 * @param {string} fieldName
 * @param {string} destinationParentName
 * @returns {{builder: object, error?: string}}
 */
export const cloneFieldToDestination = (builder, fieldName, destinationParentName) => {
  const field = builder.byId[fieldName];
  if (!field) return {builder, error: "Field not found."};

  const {label, name, parentName: sourceParentName} = field;

  let newLabel = label;

  if (labelAlreadyExists(newLabel, null, destinationParentName, builder))
    newLabel = `${label} COPY`;

  if (labelAlreadyExists(newLabel, null, destinationParentName, builder)) {
    const parentLabel = destinationParentName ? builder.byId[destinationParentName]?.label : null;
    return {
      builder,
      error: parentLabel
        ? `${newLabel} already exists in ${parentLabel}`
        : `${newLabel} already exists on top level`
    };
  }

  const sourceParent = builder.byId[sourceParentName];
  let foundHelp;
  if (sourceParent?.help) {
    foundHelp = sourceParent.help.find(({id}) => id === name);
  }

  const clone = {
    ...field,
    label: newLabel,
    name: null,
    toggle: true,
    condition: null,
    parentName: destinationParentName || "",
    help: foundHelp?.value
  };

  return {builder: addField(builder, clone)};
};

/**
 * @param {object} builder
 * @returns {Array<{value: string, label: string}>}
 */
export const getBulkCreateDestinations = builder => {
  const destinations = [];

  const recurseById = ids => {
    ids.forEach(id => {
      const item = builder.byId[id];
      if (!item?.toggle || item.deleted) return;
      if (item.element === GROUP) {
        destinations.push({value: item.name, label: item.label});
        if (item.children && item.children.length > 0) {
          recurseById(item.children);
        }
      } else if (item.element === COMPONENT) {
        const groupLabel = item.parentName ? builder.byId[item.parentName]?.label : null;
        destinations.push({
          value: item.name,
          label: groupLabel ? `${groupLabel} > ${item.label}` : item.label
        });
      }
    });
  };

  recurseById(builder.allIds);

  return destinations;
};

/**
 * @param {object} builder
 * @param {string} label
 * @param {string} destination
 * @returns {string|null}
 */
export const getBulkCreateDestinationLabelError = (builder, label, destination) => {
  if (!label || !labelAlreadyExists(label, null, destination, builder)) return null;

  const parentLabel = destination ? builder.byId[destination]?.label : null;
  return parentLabel
    ? `Field name already exists in ${parentLabel}`
    : "Field name already exists on top level";
};

/**
 * Soonest calendar date on or after max(fromDate, minDate) whose weekday is in `days`
 * (dayjs title-case names: Sun…Sat).
 *
 * @param {{days?: string[], fromDate?: string|import("dayjs").Dayjs, minDate?: string|import("dayjs").Dayjs}} [opts]
 * @returns {import("dayjs").Dayjs|null}
 */
export const nextStartOnSelectedDays = ({days, fromDate, minDate} = {}) => {
  if (!Array.isArray(days) || days.length === 0) return null;

  let start = dayjs(fromDate || undefined).startOf("day");
  if (!start.isValid()) return null;

  const floor = minDate ? dayjs(minDate).startOf("day") : null;
  if (floor?.isValid() && start.isBefore(floor, "day")) start = floor;

  for (let offset = 0; offset < 7; offset++) {
    const candidate = start.add(offset, "day");
    if (days.includes(IDX_TO_DAY_DAYJS[candidate.day()])) return candidate;
  }

  return null;
};
